import { unwrapResult } from '@reduxjs/toolkit';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DataTable from '../../../components/DataTable';
import DownloadHelper from '../../../components/DownloadHelper';
import { LoadingState } from '../../../utilities/constants';
import { fetchEmissionDataUploads, getDownloadLink } from './uploadDataSlice';
import { formatDate, parseUserInfo } from '../../../utilities/common';
import { FiAlertCircle } from 'react-icons/fi';

export const handleFileDownload = async (dispatch, upldflId, errorFile) => {
  try {
    const resultAction = await dispatch(getDownloadLink({ upldflId, errorFile }));
    const response = unwrapResult(resultAction);
    return response;
  } catch (error) {
    toast.error(error.message);
  }
};

const UploadDataTable = ({ uploadedReload, setUploadedReload }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { status, uploads } = useSelector((state) => state.uploadData);
  const sortedUploads = [...uploads].sort((a, b) => a.upldflId - b.upldflId);
  const [isInit, setIsInit] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isReloadData, setIsReloadData] = useState(false);
  parseUserInfo(useSelector((state) => state.users.user));

  const handleShowError = (message) => {
    setErrorMessage(message);
  };

  const tableColumns = useMemo(
    () => [
      {
        Header: t('Name'),
        accessor: 'upldflName',
        Cell: ({ row, value }) => {
          if (row.original.upldflSourceType === 'EMISSION_PERSE' && row.original.upldflStatus === 'UPLOADED') {
            return <Button variant='link' disabled>{value}</Button>;
          }
          return (<DownloadHelper getLinkObject={() => handleFileDownload(dispatch, row.original.upldflId)} label={value} />);
        }
      },
      {
        Header: t('Type'),
        accessor: 'upldflSourceType'
      },
      {
        Header: t('File Description'),
        accessor: 'upldflDesc'
      },
      {
        Header: t('Created By'),
        accessor: 'upldflCreatedBy'
      },
      {
        Header: t('Created Date'),
        accessor: 'upldflCreatedDate',
        Cell: ({ value }) => formatDate(value)
      },
      {
        Header: t('Status'),
        accessor: 'upldflStatus'
      },
      {
        Header: t('Errors'),
        accessor: 'upldflErrorCount',
        Cell: ({ row, value }) => {
          if (value > 0 && row.original.upldflSourceType !== 'EMISSION_PERSE') {
            return (
              <DownloadHelper
                getLinkObject={() => handleFileDownload(dispatch, row.original.upldflId, true)}
                label={value}
              />
            );
          }
          if (row.original.upldflStatusMessage && row.original.upldflStatus !== 'SUCCESSFUL') {
            return (
              <Button variant="link" onClick={() => handleShowError(row.original.upldflStatusMessage)}>
                <FiAlertCircle style={{ color: '#f00808' }} className="ml-1" />
              </Button>
            );
          }
          return (<span style={{ padding: '0.375rem 0.75rem' }}>{value}</span>);
        }
      }
    ],
    [t, dispatch]
  );

  useEffect(() => {
    const dispatchFetchData = () => dispatch(fetchEmissionDataUploads());
    if (!isInit) {
      setIsInit(true);
      dispatchFetchData();
    }
    if (uploadedReload > 0) {
      setTimeout(() => {
        setIsReloadData(true);
        dispatchFetchData();
        setUploadedReload(uploadedReload - 1);
      }, 10 * 1000);
    }
  }, [dispatch, isInit, setUploadedReload, uploadedReload]);

  if (status === LoadingState.loading && !isReloadData) return <Spinner animation="grow" />;

  return (
    <div>
      <DataTable
        columns={tableColumns}
        data={sortedUploads.map((upload) => ({
          ...upload,
          upldflCreatedDate: upload.upldflCreatedDate.substring(0, 10)
        }))}
        // data={[...uploads].sort((a, b) => a.upldflId - b.upldflId)}
        defaultSort="Uploaded Date"
      />
      <Modal show={errorMessage !== null} onHide={() => setErrorMessage(null)}>
        <Modal.Header>
          <Modal.Title>
            {t('Error Details')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setErrorMessage(null)}
          >
            {t('Close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UploadDataTable;
